"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react"
import { PublicNavGlass, type GlassNavMode } from "@/components/public-nav-glass"
import { PublicFooter } from "@/components/public-footer"
import { LocaleLink } from "@/components/locale-link"
import { GlassSurface, Reveal, displayHeadingStyle, usePrefersReducedMotion } from "@/components/glass/glass-kit"
import { HelpPlate } from "@/components/help/help-plates"
import { JournalCategoryLabel } from "@/components/journal-category-label"
import { HelpShare } from "@/components/help/help-share"
import { AUDIENCE_LABEL, type Guide } from "@/lib/help-guides"

/**
 * A single guide, built as a reading surface rather than a document dump.
 *
 * Three things carry it:
 *  1. a hairline progress rule pinned under the nav, so the reader always
 *     knows how much is left;
 *  2. a sticky numbered outline that tracks the step you're actually looking
 *     at (scroll-spy, not scroll position maths);
 *  3. steps that arrive as you reach them, each one opened by its figure
 *     number on a hairline rule.
 *
 * Every scroll-linked flourish here is off under `prefers-reduced-motion`.
 */

/** Fraction of the article scrolled through, 0 → 1. rAF-coalesced. */
function useReadingProgress(ref: React.RefObject<HTMLElement | null>) {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    let frame = 0
    const measure = () => {
      frame = 0
      const el = ref.current
      if (!el) return
      const start = el.offsetTop
      // The last viewport of the article can't be scrolled past, so the
      // denominator is its height minus one screen — otherwise the bar never
      // reaches the end on a short guide.
      const span = Math.max(el.offsetHeight - window.innerHeight * 0.7, 1)
      const scrolled = window.scrollY - start
      setProgress(Math.min(Math.max(scrolled / span, 0), 1))
    }
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(measure)
    }
    measure()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [ref])

  return progress
}

/** Which section is currently in the reading band (the middle of the screen). */
function useScrollSpy(ids: string[]) {
  const [active, setActive] = useState(ids[0] ?? "")

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return
    const seen = new Map<string, number>()
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) seen.set(e.target.id, e.intersectionRatio)
        // Topmost section still in the band wins, so scrolling up doesn't
        // leave the outline pointing at something below the fold.
        const inBand = ids.filter((id) => (seen.get(id) ?? 0) > 0)
        if (inBand.length) setActive(inBand[0])
      },
      { rootMargin: "-42% 0px -45% 0px", threshold: [0, 1] },
    )
    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) io.observe(el)
    })
    return () => io.disconnect()
  }, [ids])

  return active
}

// Sentence case at small sizes is the house hierarchy — no uppercase
// letterspaced eyebrows anywhere on this page. Colour, not caps, is what
// separates a category label from body copy.
//
// Display sizes live here as inline styles, not `text-[clamp(...)]` classes:
// an arbitrary Tailwind value that misses the build silently renders at the
// base size, and a 4rem headline dropping to 16px destroys the page.
const TITLE = "clamp(1.9rem, 4.4vw, 3.1rem)"
const STEP_TITLE = "clamp(1.02rem, 1.7vw, 1.22rem)"
const NEXT_TITLE = "clamp(1.3rem, 2.7vw, 1.85rem)"

export default function HelpGuideClient({ guide, next }: { guide: Guide; next: Guide }) {
  const [mode, setMode] = useState<GlassNavMode>("dark")
  const articleRef = useRef<HTMLDivElement>(null)
  const reduced = usePrefersReducedMotion()
  const progress = useReadingProgress(articleRef)

  // One derived value carries the guide's hue through the whole page: the
  // reading rule, the category dot, every step numeral, the live outline
  // marker and both CTAs.
  const accent = `rgb(${guide.accent})`

  const stepIds = guide.steps.map((_, i) => `step-${i + 1}`)
  const active = useScrollSpy(stepIds)

  const jumpTo = useCallback(
    (id: string) => {
      const el = document.getElementById(id)
      if (!el) return
      el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" })
    },
    [reduced],
  )

  return (
    <GlassSurface mode={mode}>
      <PublicNavGlass mode={mode} onModeChange={setMode} />

      {/* Reading rule. A hairline across the top of the page that fills as you
          read — the quietest possible progress indicator. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-px"
        style={{ background: "var(--pg-line)" }}
      >
        <div
          className="h-full origin-left"
          style={{
            background: accent,
            transform: `scaleX(${reduced ? 0 : progress})`,
            transition: "transform 120ms linear",
            opacity: 0.85,
          }}
        />
      </div>

      <main className="mx-auto w-full max-w-6xl px-5 pb-24 pt-28 sm:px-8 sm:pt-36">
        {/* ── Hero ────────────────────────────────────────────────── */}
        <header>
          <Reveal>
            <LocaleLink
              href="/blog?type=guides"
              className="inline-flex items-center gap-2 text-[12.5px] transition-opacity hover:opacity-60"
              style={{ color: "var(--pg-muted)" }}
            >
              <ArrowLeft className="h-3 w-3" strokeWidth={1.75} />
              All guides
            </LocaleLink>
          </Reveal>

          <Reveal delay={60}>
            <div
              className="mt-8 flex flex-wrap items-center gap-2 pb-5 text-[12.5px]"
              style={{ color: "var(--pg-muted)", borderBottom: "1px solid var(--pg-line)" }}
            >
              <span className="inline-flex items-center gap-1">
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ background: accent, boxShadow: `0 0 12px rgb(${guide.accent} / 0.75)` }}
                />
                <JournalCategoryLabel
                  label={AUDIENCE_LABEL[guide.audience]}
                  style={{ color: `rgb(${guide.accent} / 0.9)` }}
                />
              </span>
              <span aria-hidden style={{ opacity: 0.55 }}>
                ·
              </span>
              <span>{guide.minutes} min</span>
              <span aria-hidden style={{ opacity: 0.55 }}>
                ·
              </span>
              <span>{guide.steps.length} steps</span>
            </div>

            {/* Title full-measure, lede hanging off its right edge — same
                asymmetry the index masthead uses, so a guide reads as a page
                torn out of the same sheet. */}
            <h1 className="mt-9 font-semibold" style={displayHeadingStyle(TITLE, "-0.045em")}>
              {guide.title}
            </h1>
            <p className="mt-5 max-w-xl text-[15px] leading-[1.7]" style={{ color: "var(--pg-muted)" }}>
              {guide.summary}
            </p>

            {/* The figure runs wide here rather than sitting in a sidebar —
                it's the establishing shot for the whole guide. */}
            <HelpPlate
              kind={guide.plate}
              figure={1}
              caption={guide.plateCaption}
              accent={guide.accent}
              brand={guide.brand}
              live
              ratio="21 / 9"
              className="mt-10 w-full"
            />

            {guide.brand && (
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <span
                  className="inline-flex items-center gap-2.5 rounded-full py-1.5 pl-3 pr-3.5"
                  style={{ background: "var(--pg-card)", border: "1px solid var(--pg-card-edge)" }}
                >
                  <Image
                    src={guide.brand.src}
                    alt={guide.brand.alt}
                    width={44}
                    height={19}
                    className="h-[14px] w-auto object-contain"
                  />
                </span>
                <span className="max-w-sm text-[11.5px] leading-relaxed" style={{ color: "var(--pg-muted)" }}>
                  {guide.brand.note}
                </span>
              </div>
            )}

            <HelpShare
              slug={guide.slug}
              title={guide.title}
              summary={guide.summary}
              accent={guide.accent}
            />
          </Reveal>
        </header>

        {/* ── Body ────────────────────────────────────────────────── */}
        <div ref={articleRef} className="mt-20 flex flex-col gap-12 sm:mt-28 md:flex-row md:gap-14">
          {/* Outline. Sticky on desktop; on mobile the steps are numbered
              clearly enough that a duplicate list would just be noise. */}
          <nav aria-label="Guide contents" className="hidden shrink-0 md:block md:w-56 lg:w-64">
            <div className="sticky top-28">
              <p
                className="pb-3.5 text-[12.5px]"
                style={{ color: "var(--pg-muted)", borderBottom: "1px solid var(--pg-line)" }}
              >
                Contents
              </p>
              <ol className="mt-4 flex flex-col">
                {guide.steps.map((s, i) => {
                  const id = stepIds[i]
                  const on = active === id
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        onClick={() => jumpTo(id)}
                        className="group flex w-full items-start gap-3 py-2 text-left"
                      >
                        {/* The marker rule grows when the step is live —
                            an outline you can read at a glance from the edge. */}
                        <span
                          aria-hidden
                          className="mt-[9px] h-px shrink-0"
                          style={{
                            width: on ? 22 : 10,
                            background: on ? accent : "var(--pg-faint)",
                            transition: "width 500ms cubic-bezier(0.22,1,0.36,1), background 300ms ease",
                          }}
                        />
                        <span
                          className="text-[13px] leading-snug transition-colors duration-300"
                          style={{ color: on ? "var(--pg-fg)" : "var(--pg-muted)" }}
                        >
                          {s.title}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ol>

              {guide.cta && (
                <Link
                  href={guide.cta.href}
                  className="mt-7 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12.5px] font-medium transition-opacity duration-300 hover:opacity-80"
                  style={{ background: accent, color: "#0B0B0D" }}
                >
                  {guide.cta.label}
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                </Link>
              )}
            </div>
          </nav>

          <article className="min-w-0 flex-1">
            {guide.steps.map((s, i) => (
              <section
                key={s.title}
                id={stepIds[i]}
                className="scroll-mt-28 pt-10 first:pt-0 sm:pt-14"
              >
                <Reveal>
                  <div className={i === 0 ? "" : "pt-10 sm:pt-14"}>
                    <span className="text-[12.5px] font-medium" style={{ color: accent }}>
                      Step {i + 1}
                    </span>
                    <h2
                      className="mt-2.5 font-normal"
                      style={{
                        fontSize: STEP_TITLE,
                        lineHeight: 1.35,
                        letterSpacing: "-0.015em",
                        color: "var(--pg-fg)",
                      }}
                    >
                      {s.title}
                    </h2>
                    <p className="mt-3 max-w-xl text-[14.5px] leading-[1.75]" style={{ color: "var(--pg-muted)" }}>
                      {s.body}
                    </p>
                  </div>
                </Reveal>
              </section>
            ))}

            {guide.tips && guide.tips.length > 0 && (
              <Reveal>
                <div className="mt-14 pt-10 sm:mt-20 sm:pt-14">
                  <h2
                    className="font-normal"
                    style={{
                      fontSize: STEP_TITLE,
                      lineHeight: 1.35,
                      letterSpacing: "-0.015em",
                      color: "var(--pg-fg)",
                    }}
                  >
                    Notes
                  </h2>
                  <ul className="mt-6 flex flex-col">
                    {guide.tips.map((tip, i) => (
                      <li
                        key={tip}
                        className="flex gap-5 py-5"
                      >
                        <span className="mt-[3px] text-[12.5px] font-medium" style={{ color: accent }}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="max-w-xl text-[14.5px] leading-[1.7]" style={{ color: "var(--pg-muted)" }}>
                          {tip}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}

            {/* Mobile CTA — the sticky outline that carries it on desktop
                isn't rendered down here. */}
            {guide.cta && (
              <Reveal>
                <Link
                  href={guide.cta.href}
                  className="mt-10 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-medium transition-opacity duration-300 hover:opacity-80 md:hidden"
                  style={{ background: accent, color: "#0B0B0D" }}
                >
                  {guide.cta.label}
                  <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                </Link>
              </Reveal>
            )}
          </article>
        </div>

        {/* ── Next ────────────────────────────────────────────────── */}
        <Reveal>
          <LocaleLink
            href={`/help/${next.slug}`}
            className="group mt-24 block pt-10 sm:mt-32"
            style={{ borderTop: "1px solid var(--pg-line)" }}
          >
            <div className="flex flex-col gap-7 md:flex-row md:items-center md:gap-12">
              <div className="min-w-0 flex-1">
                <span className="text-[12.5px]" style={{ color: "var(--pg-muted)" }}>
                  Next guide
                </span>
                <h2
                  className="mt-3 font-medium transition-transform duration-500 md:group-hover:translate-x-2"
                  style={{ fontSize: NEXT_TITLE, lineHeight: 1.18, letterSpacing: "-0.03em" }}
                >
                  {next.title}
                </h2>
                <span
                  className="mt-4 inline-flex items-center gap-2 text-[12.5px]"
                  style={{ color: `rgb(${next.accent})` }}
                >
                  {AUDIENCE_LABEL[next.audience]}
                  <ArrowUpRight
                    className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                    strokeWidth={1.6}
                  />
                </span>
              </div>
              <div className="w-full shrink-0 md:w-1/2">
                <HelpPlate
                  kind={next.plate}
                  figure={2}
                  caption={next.plateCaption}
                  accent={next.accent}
                  className="w-full transition-transform duration-700 ease-out md:group-hover:-translate-y-2"
                />
              </div>
            </div>
          </LocaleLink>
        </Reveal>
      </main>
      <PublicFooter />
    </GlassSurface>
  )
}
